import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useDataCoordinator } from "@/hooks/useDataCoordinator";
import AvatarPlaceholder from "@/components/ui/avatar-placeholder";

interface EmailCampaign {
  id: number;
  subject: string;
  recipient: string;
  content: string;
  formatted_content?: string;
  sent_at: string;
  opened_at?: string;
  clicked?: boolean;
  template_id?: number;
  player_id?: number;
  user_id: number;
  message_id?: string;
  status?: string;
  track_opens?: boolean;
  track_clicks?: boolean;
  created_at: string;
  updated_at?: string;
}

interface Campaign {
  id: number;
  subject: string;
  recipients: string[];
  sentAt: string;
  emails: EmailCampaign[];
  templateId?: number;
}

export default function CampaignHistory() {
  const [showAll, setShowAll] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Get player data for matching with emails
  const { players } = useDataCoordinator();

  const { data: emails, isLoading } = useQuery<EmailCampaign[]>({
    queryKey: ["/api/emails/sent-emails"],
  });

  // Helper function to find player by ID
  const getPlayerById = (playerId?: number) => {
    if (!playerId || !players) return null;
    return players.find(player => player.id === playerId);
  };

  // Group emails by subject and date to create campaign-like view
  const campaigns = emails ? Object.values(
    emails.reduce((acc: Record<string, any>, email) => {
      const key = `${email.subject}_${email.sent_at ? new Date(email.sent_at).toDateString() : 'draft'}`;
      if (!acc[key]) {
        acc[key] = {
          id: email.id,
          subject: email.subject,
          recipients: [email.recipient],
          sentAt: email.sent_at,
          emails: [email],
          templateId: email.template_id,
        };
      } else {
        acc[key].recipients.push(email.recipient);
        acc[key].emails.push(email);
      }
      return acc;
    }, {})
  ).sort((a, b) => {
    if (!a.sentAt) return 1;
    if (!b.sentAt) return -1;
    return new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime();
  }) : [];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleViewDetails = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setShowDetailsModal(true);
  };

  const hasEmptyState = campaigns.length === 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading campaign history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-gray-900">Campaign History</h2>
        {!hasEmptyState && (
          <button
            onClick={() => setShowAll(!showAll)}
            className="text-blue-600 hover:text-blue-800 font-medium text-sm"
          >
            View All
          </button>
        )}
      </div>

      {/* Content */}
      {hasEmptyState ? (
        /* Empty State */
        <div className="bg-white rounded-lg border border-gray-200 p-12">
          <div className="text-center">
            <div className="mb-4">
              <i className="fas fa-envelope text-gray-300 text-6xl"></i>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No emails sent yet</h3>
            <p className="text-gray-500">Start by composing your first email to see analytics here.</p>
          </div>
        </div>
      ) : (
        /* Campaign List */
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
              <div className="col-span-5">Campaign</div>
              <div className="col-span-2">Recipients</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-3">Actions</div>
            </div>
          </div>
          
          <div className="divide-y divide-gray-200">
            {(showAll ? campaigns : campaigns.slice(0, 5)).map((campaign: Campaign) => {
              const openedCount = campaign.emails.filter((e: EmailCampaign) => e.opened_at).length;
              const clickedCount = campaign.emails.filter((e: EmailCampaign) => e.clicked).length;
              const openRate = campaign.emails.length > 0 ? (openedCount / campaign.emails.length * 100).toFixed(1) : '0';
              const clickRate = campaign.emails.length > 0 ? (clickedCount / campaign.emails.length * 100).toFixed(1) : '0';
              
              return (
                <div key={`${campaign.subject}_${campaign.sentAt}`} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="grid grid-cols-12 gap-4 items-center">
                    {/* Campaign Info */}
                    <div className="col-span-5">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {campaign.subject}
                          </p>
                          <div className="flex items-center space-x-2 text-xs text-gray-500">
                            <span>{campaign.sentAt ? formatDate(campaign.sentAt) : 'Draft'}</span>
                            {campaign.templateId && (
                              <>
                                <span>•</span>
                                <span>Template Used</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Recipients */}
                    <div className="col-span-2">
                      <span className="text-sm text-gray-900">{campaign.recipients.length}</span>
                    </div>
                    
                    {/* Status */}
                    <div className="col-span-2">
                      <div className="flex items-center space-x-2">
                        <i className="fas fa-paper-plane text-green-500 text-xs"></i>
                        <span className="text-xs text-gray-600">Sent</span>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="col-span-3">
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-blue-600 hover:text-blue-800 text-sm"
                          onClick={() => handleViewDetails(campaign)}
                        >
                          <i className="fas fa-eye mr-1"></i>
                          View Details
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Show More Button */}
          {!showAll && campaigns.length > 5 && (
            <div className="px-6 py-4 border-t border-gray-200 text-center">
              <button
                onClick={() => setShowAll(true)}
                className="text-blue-600 hover:text-blue-800 font-medium text-sm"
              >
                View {campaigns.length - 5} more campaigns
              </button>
            </div>
          )}
        </div>
      )}

      {/* Campaign Details Modal */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <i className="fas fa-envelope text-blue-600"></i>
              <span>Campaign Details</span>
            </DialogTitle>
            <DialogDescription>
              Detailed information about your email campaign
            </DialogDescription>
          </DialogHeader>
          
          {selectedCampaign && (
            <div className="flex-1 overflow-y-auto">
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="recipients">Recipients</TabsTrigger>
                  <TabsTrigger value="content">Content</TabsTrigger>
                </TabsList>
                
                <TabsContent value="overview" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700">Subject</h4>
                        <p className="text-sm text-gray-900">{selectedCampaign.subject}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-700">Sent Date</h4>
                        <p className="text-sm text-gray-900">
                          {selectedCampaign.sentAt ? formatDateTime(selectedCampaign.sentAt) : 'Draft'}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-700">Template Used</h4>
                        <p className="text-sm text-gray-900">
                          {selectedCampaign.templateId ? (
                            <Badge variant="secondary">Template #{selectedCampaign.templateId}</Badge>
                          ) : (
                            <span className="text-gray-500">No template used</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <h4 className="text-sm font-medium text-gray-700">Total Recipients</h4>
                        <p className="text-2xl font-bold text-blue-600">{selectedCampaign.emails.length}</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-gray-700">Delivery Status</h4>
                        <div className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Sent</span>
                            <span className="text-sm font-medium">{selectedCampaign.emails.length}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Opened</span>
                            <span className="text-sm font-medium text-green-600">
                              {selectedCampaign.emails.filter(e => e.opened_at).length}
                            </span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">Clicked</span>
                            <span className="text-sm font-medium text-blue-600">
                              {selectedCampaign.emails.filter(e => e.clicked).length}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="recipients" className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Email Recipients ({selectedCampaign.emails.length})</h4>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="max-h-64 overflow-y-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Player</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Email</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {selectedCampaign.emails.map((email, index) => {
                              const player = getPlayerById(email.player_id);
                              return (
                                <tr key={index} className="hover:bg-gray-50">
                                  <td className="px-4 py-2">
                                    {player ? (
                                      <div className="flex items-center space-x-2">
                                        <AvatarPlaceholder name={player.name} className="w-6 h-6 flex-shrink-0" />
                                        <div className="min-w-0">
                                          <p className="text-sm font-medium text-gray-900 truncate">{player.name}</p>
                                          <p className="text-xs text-gray-500 truncate">
                                            {player.position?.join(', ') || 'Unknown'} • {player.age || 'N/A'}y
                                          </p>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="flex items-center space-x-2">
                                        <div className="w-6 h-6 bg-gray-200 rounded-full flex-shrink-0 flex items-center justify-center">
                                          <i className="fas fa-user text-gray-400 text-xs"></i>
                                        </div>
                                        <span className="text-sm text-gray-500">Unknown Player</span>
                                      </div>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900">{email.recipient}</td>
                                  <td className="px-4 py-2">
                                    <Badge variant="default" className="text-xs">
                                      <i className="fas fa-paper-plane mr-1"></i>
                                      Sent
                                    </Badge>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="content" className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Email Content</h4>
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="prose prose-sm max-w-none">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: selectedCampaign.emails[0]?.formatted_content || 
                                   selectedCampaign.emails[0]?.content?.replace(/\n/g, '<br>') || 
                                   'No content available'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}